import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Sparkles,
  Truck,
  Shield,
  ChevronLeft,
  ChevronRight,
  Headphones,
  Layers,
  Video,
  Play,
  ExternalLink,
  X,
} from 'lucide-react';
import { useCatalog } from '../../hooks/use-catalog';
import { useBrands } from '../../hooks/products/use-brands';
import { useClients } from '../../hooks/clients/use-clients';
import { CategoryEntity } from '../../../domain/entities/category.entity';
import { BannerEntity } from '../../../domain/entities/banner.entity';
import { BannerSettingsEntity } from '../../../domain/entities/banner-settings.entity';
import { QualityEntity } from '../../../domain/entities/quality.entity';
import { YoutubeLinkEntity } from '../../../domain/entities/youtube-link.entity';
import { BannerSlider } from '../../components/ui/banner-slider';
import { SocialFeed } from '../../components/ui/social-feed';
import { TechAmbientBackground } from '../../components/ui/tech-ambient-background';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { SeoHead } from '../../components/seo/seo-head';

const DEFAULT_QUALITIES = [
  new QualityEntity({
    id: 'default-1',
    title: 'Servicio Técnico Propio y Laboratorio',
    description:
      'Diagnóstico, calibración de cabezales térmicos, mantención preventiva y disponibilidad permanente de repuestos en Ñuñoa, Santiago.',
    displayOrder: 1,
    isActive: true,
  }),
  new QualityEntity({
    id: 'default-2',
    title: 'Validación de Compatibilidad de Software',
    description:
      'Asesoramos en la homologación de drivers con software POS, sistemas de facturación electrónica SII, ERPs comerciales y aplicaciones móviles.',
    displayOrder: 2,
    isActive: true,
  }),
  new QualityEntity({
    id: 'default-3',
    title: 'Atención y Precios Especiales para Empresas',
    description:
      'Condiciones comerciales preferenciales, cotizaciones rápidas y despacho coordinado para proyectos, empresas y compras por volumen.',
    displayOrder: 3,
    isActive: true,
  }),
];

const DEFAULT_VIDEOS = [
  new YoutubeLinkEntity({
    id: 'demo-1',
    title: 'Terminales Touch Todo en Uno POS: Resistencia & Rendimiento',
    description:
      'Demostración de ensamblaje, pantalla touch capacitiva sellada IP54 y pruebas de velocidad en cajas registradoras.',
    youtubeUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    displayOrder: 1,
    isActive: true,
  }),
  new YoutubeLinkEntity({
    id: 'demo-2',
    title: 'Impresoras Térmicas de Boletas 80mm: Velocidad & Auto-Cutter',
    description:
      'Prueba de impresión continua a 250 mm/s, recambio rápido de rollo de papel drop-in y corte automático de guillotina.',
    youtubeUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    displayOrder: 2,
    isActive: true,
  }),
  new YoutubeLinkEntity({
    id: 'demo-3',
    title: 'Lectores de Códigos de Barra 1D y 2D / QR de Alta Densidad',
    description:
      'Lectura omnidireccional en pantallas de celular, tickets arrugados o reflectantes para retail y centros logísticos.',
    youtubeUrl: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    displayOrder: 3,
    isActive: true,
  }),
];

const chunkArray = <T,>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const buildMarqueeBrands = <T,>(brandsList: T[]): T[] => {
  if (!brandsList || brandsList.length === 0) return [];
  const minItemsPerHalf = 18;
  const repeatTimes = Math.max(2, Math.ceil(minItemsPerHalf / brandsList.length));
  let singleHalf: T[] = [];
  for (let i = 0; i < repeatTimes; i++) {
    singleHalf = singleHalf.concat(brandsList);
  }
  return [...singleHalf, ...singleHalf];
};

export const HomePage: React.FC = () => {
  const {
    getCategoriesUseCase,
    getBannersUseCase,
    getBannerSettingsUseCase,
    getQualitiesUseCase,
    getYoutubeLinksUseCase,
  } = useCatalog();
  const { brands } = useBrands();
  const { clients } = useClients();
  const [categories, setCategories] = useState<CategoryEntity[]>([]);
  const [banners, setBanners] = useState<BannerEntity[]>([]);
  const [isBannersLoading, setIsBannersLoading] = useState<boolean>(true);
  const [bannerSettings, setBannerSettings] = useState<BannerSettingsEntity>(new BannerSettingsEntity());
  const [qualities, setQualities] = useState<QualityEntity[]>([]);
  const [videos, setVideos] = useState<YoutubeLinkEntity[]>([]);
  const [currentQualitySlide, setCurrentQualitySlide] = useState<number>(0);
  const [isQualityPaused, setIsQualityPaused] = useState<boolean>(false);
  const [currentVideoSlide, setCurrentVideoSlide] = useState<number>(0);
  const [isVideoPaused, setIsVideoPaused] = useState<boolean>(false);
  const [activeModalVideo, setActiveModalVideo] = useState<YoutubeLinkEntity | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [catsRes, bannerListRes, bannerSettingsRes, qualitiesRes, videosRes] = await Promise.allSettled([
          getCategoriesUseCase.execute({ activeOnly: true }),
          getBannersUseCase.execute(true),
          getBannerSettingsUseCase ? getBannerSettingsUseCase.execute() : Promise.resolve(new BannerSettingsEntity()),
          getQualitiesUseCase.execute({ activeOnly: true }),
          getYoutubeLinksUseCase.execute({ activeOnly: true }),
        ]);
        if (catsRes.status === 'fulfilled') {
          const allActive = catsRes.value;
          const homeOnly = allActive.filter((c) => c.showOnHome);
          setCategories(homeOnly.length > 0 ? homeOnly : allActive);
        }
        if (bannerListRes.status === 'fulfilled') setBanners(bannerListRes.value);
        if (bannerSettingsRes.status === 'fulfilled') setBannerSettings(bannerSettingsRes.value);
        if (qualitiesRes.status === 'fulfilled') setQualities(qualitiesRes.value);
        if (videosRes.status === 'fulfilled') setVideos(videosRes.value);
      } catch (err) {
        console.error('Error cargando datos de Home:', err);
      } finally {
        setIsBannersLoading(false);
      }
    };
    loadData();
  }, [getCategoriesUseCase, getBannersUseCase, getBannerSettingsUseCase, getQualitiesUseCase, getYoutubeLinksUseCase]);

  // Genera lista simétrica y suficientemente ancha para carrusel continuo e infinito sin cortes
  const marqueeBrands = buildMarqueeBrands(brands);

  // Si aún no se registran cualidades en la base de datos, mostramos los pilares iniciales
  const displayQualities = qualities.length > 0 ? qualities : DEFAULT_QUALITIES;

  // Agrupamos las cualidades de 3 en 3 para el carrusel
  const qualityPages = chunkArray(displayQualities, 3);

  const nextQualitySlide = useCallback(() => {
    if (qualityPages.length === 0) return;
    setCurrentQualitySlide((prev) => (prev + 1) % qualityPages.length);
  }, [qualityPages.length]);

  const prevQualitySlide = useCallback(() => {
    if (qualityPages.length === 0) return;
    setCurrentQualitySlide((prev) => (prev - 1 + qualityPages.length) % qualityPages.length);
  }, [qualityPages.length]);

  // Auto-play suave cada 6.5 segundos (se pausa al pasar el mouse por encima)
  useEffect(() => {
    if (qualityPages.length <= 1 || isQualityPaused) return;
    const timer = setInterval(nextQualitySlide, 6500);
    return () => clearInterval(timer);
  }, [qualityPages.length, isQualityPaused, nextQualitySlide]);

  // Si aún no se registran videos en base de datos, mostramos cápsulas demostrativas
  const displayVideos = videos.length > 0 ? videos : DEFAULT_VIDEOS;

  // Agrupamos los videos de 3 en 3 para el carrusel
  const videoPages = chunkArray(displayVideos, 3);

  const nextVideoSlide = useCallback(() => {
    setCurrentVideoSlide((prev) => (prev + 1) % videoPages.length);
  }, [videoPages.length]);

  const prevVideoSlide = useCallback(() => {
    setCurrentVideoSlide((prev) => (prev - 1 + videoPages.length) % videoPages.length);
  }, [videoPages.length]);

  // Auto-play suave para videos cada 7.5 segundos (se pausa con hover)
  useEffect(() => {
    if (videoPages.length <= 1 || isVideoPaused) return;
    const timer = setInterval(nextVideoSlide, 7500);
    return () => clearInterval(timer);
  }, [videoPages.length, isVideoPaused, nextVideoSlide]);

  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://bartech.cl/#organization',
        'name': 'BARTECH - Tecnología e Ingeniería SpA',
        'url': 'https://bartech.cl',
        'logo': 'https://bartech.cl/bartech_logo.jpg',
        'description':
          'Distribuidor B2B e integrador oficial en Chile de hardware para retail, bodegas e industria. Capturadores PDA, Scanners Honeywell, Robots con IA, RFID, POS y servicio técnico homologado.',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': 'Simón Bolívar 2389-A',
          'addressLocality': 'Ñuñoa, Santiago',
          'addressRegion': 'Región Metropolitana',
          'addressCountry': 'CL',
        },
        'contactPoint': {
          '@type': 'ContactPoint',
          'telephone': '+56 2 2923 8600',
          'contactType': 'sales',
          'areaServed': 'CL',
          'availableLanguage': ['Spanish', 'English'],
        },
      },
      {
        '@type': 'WebSite',
        '@id': 'https://bartech.cl/#website',
        'url': 'https://bartech.cl',
        'name': 'BARTECH Chile',
        'publisher': {
          '@id': 'https://bartech.cl/#organization',
        },
        'potentialAction': {
          '@type': 'SearchAction',
          'target': 'https://bartech.cl/catalogo?busqueda={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };

  return (
    <div className="flex flex-col">
      <SeoHead
        title="BARTECH | Hardware para Retail, Bodegas, Scanners, RFID y POS en Chile"
        description="Distribuidor B2B líder en Chile de hardware para retail, bodegas e industria. Capturadores PDA Honeywell, Scanners 1D/2D, Robots con IA, RFID, POS y servicio técnico."
        keywords="hardware retail chile, capturadores pda santiago, scanners honeywell chile, lectores rfid bodegas, cotizar terminales pos chile, robots ia retail, soporte tecnico bartech"
        canonicalUrl="https://bartech.cl/"
        jsonLd={homeJsonLd}
      />
      {/* 1. Hero Banner Slider Interactivo o Hero de Bienvenida */}
      <h1 className="sr-only">BARTECH Chile | Distribuidor e Integrador B2B de Hardware para Retail, Bodegas, Scanners 1D/2D, PDA y POS</h1>
      {isBannersLoading ? (
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-6 w-full">
          <div className="w-full aspect-[16/9] sm:aspect-[4/1] rounded-2xl bg-neutral-900/60 border border-neutral-800 animate-pulse flex items-center justify-center">
            <div className="flex items-center gap-2 text-neutral-500 text-xs sm:text-sm">
              <Sparkles className="w-4 h-4 text-[#33CC00] animate-spin" />
              <span>Cargando soluciones tecnológicas BARTECH...</span>
            </div>
          </div>
        </section>
      ) : bannerSettings.layout === 'full' && banners.length > 0 ? (
        <>
          <section className="w-full px-0 pt-0 pb-4 sm:pb-6">
            <BannerSlider
              banners={banners}
              layoutMode="full"
              heightSize={bannerSettings.size}
              autoplaySpeed={bannerSettings.autoplaySpeed}
            />
          </section>

          {/* Badges de Confianza y Respaldo alineados al contenedor */}
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 w-full">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 pt-2 sm:pt-4 border-t border-neutral-200 dark:border-neutral-800 text-xs text-neutral-600 dark:text-neutral-400">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center flex-shrink-0 border border-[#33CC00]/30 shadow-xs">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <strong className="block text-neutral-900 dark:text-white">Hardware Grado Industrial</strong>
                  <span className="text-[11px] text-neutral-500">Operación continua 24/7 sin fallas</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center flex-shrink-0 border border-[#33CC00]/30 shadow-xs">
                  <Headphones className="w-4 h-4" />
                </div>
                <div>
                  <strong className="block text-neutral-900 dark:text-white">Soporte Técnico Especializado</strong>
                  <span className="text-[11px] text-neutral-500">Mesa de ayuda y laboratorio en Chile</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center flex-shrink-0 border border-[#33CC00]/30 shadow-xs">
                  <Truck className="w-4 h-4" />
                </div>
                <div>
                  <strong className="block text-neutral-900 dark:text-white">Cobertura Nacional</strong>
                  <span className="text-[11px] text-neutral-500">Roll-out y despachos a todo Chile</span>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center flex-shrink-0 border border-[#33CC00]/30 shadow-xs">
                  <Shield className="w-4 h-4" />
                </div>
                <div>
                  <strong className="block text-neutral-900 dark:text-white">Garantía de Fábrica</strong>
                  <span className="text-[11px] text-neutral-500">Garantía oficial y repuestos</span>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-6 w-full">
          {banners.length > 0 ? (
            <BannerSlider
              banners={banners}
              layoutMode="boxed"
              heightSize={bannerSettings.size}
              autoplaySpeed={bannerSettings.autoplaySpeed}
            />
          ) : (
            <div className="relative w-full overflow-hidden bg-gradient-to-br from-neutral-900 via-neutral-950 to-neutral-900 text-white rounded-2xl shadow-xl p-8 sm:p-14 border border-neutral-800">
              <div className="max-w-2xl space-y-4">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest bg-[#33CC00]/15 text-[#33CC00] border border-[#33CC00]/40 shadow-[0_0_10px_rgba(51,204,0,0.2)]">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Tecnología e Ingeniería Comercial</span>
                </div>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
                  BARTECH: Soluciones de Hardware POS, Autoatención & Roll-Out
                </h2>
                <p className="text-sm sm:text-base text-neutral-400 max-w-xl">
                  Equipamiento de alto rendimiento, terminales touch y soporte especializado para retail, gastronomía e industria.
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/catalogo">
                    <Button variant="primary" size="md" className="gap-2 shadow-lg shadow-[#33CC00]/25 bg-[#33CC00] hover:bg-[#2EB800] text-white border-none">
                      <span>Explorar Catálogo</span>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/contacto">
                    <Button variant="secondary" size="md">
                      Cotizar / Contactar
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Badges de Confianza y Respaldo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-neutral-200 dark:border-neutral-800 text-xs text-neutral-600 dark:text-neutral-400">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center flex-shrink-0 border border-[#33CC00]/30 shadow-xs">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <strong className="block text-neutral-900 dark:text-white">Hardware Grado Industrial</strong>
                <span className="text-[11px] text-neutral-500">Operación continua 24/7 sin fallas</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center flex-shrink-0 border border-[#33CC00]/30 shadow-xs">
                <Headphones className="w-4 h-4" />
              </div>
              <div>
                <strong className="block text-neutral-900 dark:text-white">Soporte Técnico Especializado</strong>
                <span className="text-[11px] text-neutral-500">Mesa de ayuda y laboratorio en Chile</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center flex-shrink-0 border border-[#33CC00]/30 shadow-xs">
                <Truck className="w-4 h-4" />
              </div>
              <div>
                <strong className="block text-neutral-900 dark:text-white">Cobertura Nacional</strong>
                <span className="text-[11px] text-neutral-500">Roll-out y despachos a todo Chile</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#33CC00]/15 text-[#33CC00] flex items-center justify-center flex-shrink-0 border border-[#33CC00]/30 shadow-xs">
                <Shield className="w-4 h-4" />
              </div>
              <div>
                <strong className="block text-neutral-900 dark:text-white">Garantía de Fábrica</strong>
                <span className="text-[11px] text-neutral-500">Garantía oficial y repuestos</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 2. Marcas Oficiales que Comercializamos (Carrusel Continuo Dinámico en Loop Infinito) */}
      {marqueeBrands.length > 0 && (
        <section className="w-full overflow-hidden py-6 sm:py-8 select-none relative border-y border-neutral-200/60 dark:border-neutral-800/60 bg-neutral-50/40 dark:bg-neutral-900/30">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-4">
            <h2 className="text-xs font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
              Marcas Oficiales & Estándares Globales de Hardware
            </h2>
          </div>

          <div className="relative w-full overflow-hidden">
            {/* Gradientes laterales para dar sensación de fondo infinito */}
            <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 sm:w-36 lg:w-48 bg-gradient-to-r from-[var(--bg-canvas)] to-transparent z-10" />
            <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 sm:w-36 lg:w-48 bg-gradient-to-l from-[var(--bg-canvas)] to-transparent z-10" />

            {/* Pista continua moviéndose lentamente a la derecha */}
            <div className="flex w-max animate-marquee-right items-center hover:[animation-play-state:paused] py-2">
              {marqueeBrands.map((brand, index) => (
                <Link
                  key={`${brand.id}-${index}`}
                  to={`/catalogo?marca=${encodeURIComponent(brand.slug)}`}
                  className="flex-shrink-0 px-8 sm:px-12 lg:px-16 flex items-center justify-center group transition-transform duration-300 hover:scale-110"
                  title={brand.description || `Equipos de ${brand.name}`}
                >
                  {brand.logoUrl ? (
                    <img
                      src={brand.logoUrl}
                      alt={`Logo oficial de ${brand.name}`}
                      width={160}
                      height={48}
                      decoding="async"
                      className="h-10 sm:h-12 max-w-[140px] sm:max-w-[180px] lg:max-w-[200px] object-contain filter grayscale opacity-60 hover:grayscale-0 hover:opacity-100 dark:brightness-125 dark:hover:brightness-100 transition-all duration-300"
                      loading="lazy"
                      onError={(e) => {
                        (e.currentTarget as HTMLElement).style.display = 'none';
                        const fallback = (e.currentTarget as HTMLElement).nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <span
                    className={`font-extrabold text-sm sm:text-base lg:text-lg tracking-wider text-neutral-400 dark:text-neutral-500 group-hover:text-brand-primary uppercase transition-colors ${brand.logoUrl ? 'hidden' : 'block'
                      }`}
                  >
                    {brand.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contenedor con la ambientación técnica de fondo a partir de Categorías */}
      <div className="relative w-full overflow-hidden">
        <TechAmbientBackground />

        {/* 3. Categorías de Hardware & Equipamiento */}
        {categories.length > 0 && (
          <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 w-full relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 sm:mb-8">
              <div>
                <Badge variant="neutral" size="sm" className="mb-2">
                  Familias de Equipamiento
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 dark:text-white">
                  Especialidades de Hardware Comercial
                </h2>
              </div>
              <Link
                to="/catalogo"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-[#33CC00] transition-colors"
              >
                <span>Ver catálogo completo</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/catalogo?categoria=${cat.slug}`}
                  className="group p-6 rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:border-[#33CC00]/60 dark:hover:border-[#33CC00]/50 hover:shadow-[0_8px_25px_rgba(51,204,0,0.12)] transition-all duration-200 flex flex-col justify-between relative z-10"
                >
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#33CC00]/15 dark:bg-[#33CC00]/20 text-[#33CC00] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200 border border-[#33CC00]/30 shadow-xs">
                      <Layers className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-neutral-900 dark:text-white group-hover:text-brand-primary transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2 leading-relaxed">
                      {cat.description}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-xs font-semibold text-neutral-600 dark:text-neutral-300 group-hover:text-[#1a7f00] dark:group-hover:text-[#33CC00]">
                    <span>Explorar modelos</span>
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* 4. Pilares de Respaldo y Confianza (Carrusel de 3 en 3 Tarjetas) */}
        <section
          className="w-full bg-neutral-100/90 dark:bg-neutral-900/90 py-10 sm:py-14 border-y border-neutral-200 dark:border-neutral-800 relative z-10 select-none backdrop-blur-xs"
          onMouseEnter={() => setIsQualityPaused(true)}
          onMouseLeave={() => setIsQualityPaused(false)}
        >
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 sm:mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 dark:text-white">
                  ¿Por qué elegir a BARTECH?
                </h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
                  Más que proveedores de hardware, somos socios de ingeniería con más de 19 años respaldando la operación comercial en Chile.
                </p>
              </div>

              {/* Controles de Navegación Flechas (si hay más de 1 grupo) */}
              {qualityPages.length > 1 && (
                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <button
                    type="button"
                    onClick={prevQualitySlide}
                    className="p-2 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:text-brand-primary hover:border-brand-primary transition-all shadow-xs cursor-pointer"
                    title="Anterior grupo de cualidades"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={nextQualitySlide}
                    className="p-2 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:text-brand-primary hover:border-brand-primary transition-all shadow-xs cursor-pointer"
                    title="Siguiente grupo de cualidades"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            {/* Track del Carrusel */}
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-700 ease-out"
                style={{ transform: `translateX(-${currentQualitySlide * 100}%)` }}
              >
                {qualityPages.map((page, pageIndex) => (
                  <div
                    key={pageIndex}
                    className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8"
                  >
                    {page.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white dark:bg-neutral-900 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm space-y-3 flex flex-col justify-start hover:border-[#33CC00]/50 hover:shadow-[0_8px_25px_rgba(51,204,0,0.12)] transition-all duration-300 relative z-10"
                      >
                        <div className="w-10 h-10 rounded-xl bg-[#33CC00]/15 dark:bg-[#33CC00]/20 text-[#33CC00] flex items-center justify-center flex-shrink-0 border border-[#33CC00]/30 shadow-xs">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              width={20}
                              height={20}
                              loading="lazy"
                              decoding="async"
                              className="w-5 h-5 object-contain"
                            />
                          ) : (
                            <Sparkles className="w-5 h-5 text-[#33CC00]" />
                          )}
                        </div>
                        <h3 className="text-base font-bold text-neutral-900 dark:text-white">
                          {item.title}
                        </h3>
                        <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Indicadores de Paginación Dots */}
            {qualityPages.length > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                {qualityPages.map((_, dotIdx) => (
                  <button
                    key={dotIdx}
                    type="button"
                    onClick={() => setCurrentQualitySlide(dotIdx)}
                    className={`h-2 rounded-full transition-all cursor-pointer ${currentQualitySlide === dotIdx
                        ? 'w-8 bg-brand-primary'
                        : 'w-2 bg-neutral-300 dark:bg-neutral-700 hover:bg-neutral-400 dark:hover:bg-neutral-600'
                      }`}
                    aria-label={`Ir al grupo ${dotIdx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* 5. Demostraciones de Hardware & Videos en Acción (Carrusel de 3 en 3) */}
        <section
          className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 relative select-none w-full"
          onMouseEnter={() => setIsVideoPaused(true)}
          onMouseLeave={() => setIsVideoPaused(false)}
        >
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <Badge variant="brand" size="sm" className="mb-2">
                Demostraciones & Hardware en Acción
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 dark:text-white">
                Conoce Nuestros Equipos en Operación Real
              </h2>
              <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-2xl">
                Cápsulas en video con pruebas de velocidad, resistencia y puesta en marcha de nuestros equipos POS, impresoras térmicas y periféricos de autoatención.
              </p>
            </div>

            {/* Controles de Navegación Flechas (si hay más de 1 página) */}
            {videoPages.length > 1 && (
              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={prevVideoSlide}
                  className="p-2 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:text-brand-primary hover:border-brand-primary transition-all shadow-xs cursor-pointer"
                  title="Anterior grupo de videos"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={nextVideoSlide}
                  className="p-2 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:text-brand-primary hover:border-brand-primary transition-all shadow-xs cursor-pointer"
                  title="Siguiente grupo de videos"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {/* Track del Carrusel */}
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${currentVideoSlide * 100}%)` }}
            >
              {videoPages.map((page, pageIndex) => (
                <div
                  key={pageIndex}
                  className="w-full flex-shrink-0 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8"
                >
                  {page.map((video) => (
                    <div
                      key={video.id}
                      onClick={() => setActiveModalVideo(video)}
                      className="group bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm overflow-hidden flex flex-col hover:border-brand-primary/40 hover:shadow-card-hover transition-all duration-300 cursor-pointer relative z-10"
                    >
                      {/* Miniatura 16:9 con Play Overlay */}
                      <div className="relative aspect-video w-full bg-neutral-950 overflow-hidden">
                        <img
                          src={video.thumbnailUrl}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />

                        {/* Botón Central Play */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:bg-red-500 transition-all duration-300">
                            <Play className="w-5 h-5 fill-current ml-0.5" />
                          </div>
                        </div>

                        <div className="absolute bottom-2.5 right-3 px-2 py-0.5 rounded-md bg-black/80 text-[10px] font-mono text-white font-medium flex items-center gap-1">
                          <Video className="w-3 h-3 text-red-500" />
                          <span>YouTube</span>
                        </div>
                      </div>

                      {/* Datos */}
                      <div className="p-6 flex-1 flex flex-col justify-between space-y-3">
                        <div className="space-y-2">
                          <h3 className="text-base font-bold text-neutral-900 dark:text-white group-hover:text-brand-primary transition-colors line-clamp-2">
                            {video.title}
                          </h3>
                          {video.description && (
                            <p className="text-xs text-neutral-600 dark:text-neutral-400 line-clamp-2 leading-relaxed">
                              {video.description}
                            </p>
                          )}
                        </div>

                        <div className="pt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-xs font-semibold text-brand-primary">
                          <span className="flex items-center gap-1.5">
                            <Play className="w-3.5 h-3.5 fill-current" />
                            Ver demostración
                          </span>
                          <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Indicadores de Paginación Dots */}
          {videoPages.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              {videoPages.map((_, dotIdx) => (
                <button
                  key={dotIdx}
                  type="button"
                  onClick={() => setCurrentVideoSlide(dotIdx)}
                  className={`h-2 rounded-full transition-all cursor-pointer ${
                    currentVideoSlide === dotIdx
                      ? 'w-8 bg-brand-primary'
                      : 'w-2 bg-neutral-300 dark:bg-neutral-700 hover:bg-neutral-400 dark:hover:bg-neutral-600'
                  }`}
                  aria-label={`Ir al grupo de videos ${dotIdx + 1}`}
                />
              ))}
            </div>
          )}
        </section>

        {/* Modal Reproductor de YouTube Embebido */}
        {activeModalVideo && activeModalVideo.videoId && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setActiveModalVideo(null)}
          >
            <div
              className="bg-neutral-900 border border-neutral-800 rounded-2xl w-full max-w-4xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-neutral-800 text-white">
                <div className="flex items-center gap-2 max-w-xl truncate">
                  <Video className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <h3 className="text-sm sm:text-base font-bold truncate">
                    {activeModalVideo.title}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={activeModalVideo.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 text-neutral-400 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors cursor-pointer"
                    title="Ver directamente en YouTube"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setActiveModalVideo(null)}
                    className="p-1.5 text-neutral-400 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors cursor-pointer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Iframe 16:9 responsivo */}
              <div className="relative aspect-video w-full bg-black">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${activeModalVideo.videoId}?autoplay=1&rel=0`}
                  title={activeModalVideo.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              </div>

              {activeModalVideo.description && (
                <div className="p-4 bg-neutral-950 text-neutral-300 text-xs sm:text-sm border-t border-neutral-800">
                  {activeModalVideo.description}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 6. Empresas y Clientes Asociados que Confían en Bartech */}
        {clients.length > 0 && (
          <section className="w-full border-t border-neutral-200/60 dark:border-neutral-800/60 bg-neutral-50/30 dark:bg-neutral-900/20 py-10 sm:py-12 relative z-10">
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-8">
                <Badge variant="brand" size="sm" className="mb-2">
                  Confianza y Alianzas Comerciales
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 dark:text-white">
                  Grandes Empresas y Cadenas que Operan con BARTECH
                </h2>
                <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-2">
                  Implementamos infraestructura de hardware comercial y soporte técnico en las principales cadenas de retail, logística y gastronomía de Chile.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 items-center">
                {clients.map((client) => (
                  <div
                    key={client.id}
                    className="group p-5 rounded-2xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 hover:border-brand-primary/40 hover:shadow-card-hover transition-all duration-200 flex flex-col items-center justify-center text-center h-28"
                  >
                    <img
                      src={client.logoUrl}
                      alt={`Logo de ${client.name}`}
                      className="max-h-12 max-w-[85%] object-contain filter grayscale group-hover:grayscale-0 dark:brightness-150 transition-all duration-300 group-hover:scale-105"
                    />
                    {client.category && (
                      <span className="text-[10px] font-semibold text-neutral-400 mt-2 uppercase tracking-wider">
                        {client.category}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 7. Publicaciones de Redes Sociales */}
        <div className="relative z-10">
          <SocialFeed />
        </div>

        {/* 8. Banner de Conversión Final */}
        <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-12 sm:pb-16 w-full relative z-10">
          <div className="rounded-2xl bg-neutral-900 text-white p-8 sm:p-12 border border-neutral-800 relative overflow-hidden flex flex-col lg:flex-row items-center justify-between gap-8 shadow-xl">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#33CC00]/10 rounded-full blur-3xl pointer-events-none" />

            <div className="space-y-3 text-center lg:text-left relative z-10 max-w-xl">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                ¿Preparando un nuevo proyecto o apertura de sucursales?
              </h2>
              <p className="text-sm text-neutral-300 leading-relaxed">
                Comunícate con nuestros especialistas para recibir asesoría directa, fichas técnicas detalladas y cotización rápida.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 relative z-10 w-full lg:w-auto">
              <a
                href="https://wa.me/56971380593?text=Hola%20BARTECH,%20necesito%20cotizar%20un%20proyecto%20de%20hardware"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#25D366] hover:bg-[#1EBE5D] text-white font-semibold text-sm shadow-md transition-colors"
              >
                <span>Consultar por WhatsApp (+56 9 7138 0593)</span>
              </a>
              <Link to="/contacto" className="inline-flex">
                <Button variant="primary" size="lg" className="w-full">
                  <span>Formulario de Contacto</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
